/**
 * RTE-X11.5c-f — route PATCH must not carry topology (TOPOLOGY_FROZEN).
 * Topology changes go through upsertRouteStops + allocations only.
 */

export const ROUTE_ENTITY_TOPOLOGY_PATCH_KEYS = new Set([
  'transportType',
  'loadingPointType',
  'loadingPointId',
  'unloadingPointType',
  'unloadingPointId',
  'destinationType',
  'isDeliveryToConsignee',
  'shipperId',
  'consigneeId',
  'clientId'
]);

export type BuildOperationalRouteUpdateParams = {
  routeId: number;
  editingRoute: Record<string, unknown>;
  finalData: Record<string, unknown>;
  applications: Array<{
    id: number;
    shipperId?: number | null;
    consigneeId?: number | null;
    responsibleLogistId?: number | null;
  }>;
  currentUserId?: number;
  railwayStations?: Array<{ id: number; name?: string | null }>;
  airports?: Array<{ id: number; name?: string | null }>;
};

function normScalar(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value.trim();
  return String(value);
}

function stripTopologyKeys(patch: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { id: patch.id };
  for (const [key, value] of Object.entries(patch)) {
    if (key === 'id') continue;
    if (ROUTE_ENTITY_TOPOLOGY_PATCH_KEYS.has(key)) continue;
    out[key] = value;
  }
  return out;
}

/** Build PATCH body for PUT /routes/:id — allocation/stop saves must not trip TOPOLOGY_FROZEN. */
export function buildRouteOperationalUpdatePatch(
  params: BuildOperationalRouteUpdateParams
): Record<string, unknown> {
  const { finalData, editingRoute, applications } = params;

  let resolvedLoadingPointType: 'warehouse' | 'shipper' =
    finalData.loadingPointType === 'application' ? 'shipper' : 'warehouse';
  let resolvedLoadingPointId: number | undefined = finalData.loadingPointId as number | undefined;
  let resolvedShipperId: number | undefined = finalData.shipperId as number | undefined;

  if (finalData.loadingPointType === 'application' && finalData.loadingPointId) {
    const selectedApp = applications.find((app) => app.id === Number(finalData.loadingPointId));
    if (selectedApp?.shipperId) {
      resolvedLoadingPointId = selectedApp.shipperId;
      resolvedShipperId = selectedApp.shipperId;
    }
  }

  let resolvedConsigneeId: number | undefined = finalData.consigneeId as number | undefined;
  if (finalData.unloadingPointType === 'consignee') {
    resolvedConsigneeId = finalData.unloadingPointId as number | undefined;
  } else if (finalData.unloadingPointType === 'application' && finalData.unloadingPointId) {
    resolvedConsigneeId = applications.find((app) => app.id === Number(finalData.unloadingPointId))
      ?.consigneeId ?? undefined;
  }

  const raw: Record<string, unknown> = {
    id: params.routeId,
    transportType: finalData.transportType,
    loadingPointType: resolvedLoadingPointType,
    loadingPointId: resolvedLoadingPointId,
    unloadingPointType: finalData.unloadingPointType,
    unloadingPointId: finalData.unloadingPointId,
    clientId: finalData.clientId,
    expeditorId: finalData.expeditorId,
    loadingAddress: finalData.loadingAddress,
    unloadingAddress: finalData.unloadingAddress,
    loadingCity: finalData.loadingCity ? String(finalData.loadingCity).trim() : undefined,
    unloadingCity: finalData.unloadingCity ? String(finalData.unloadingCity).trim() : undefined,
    loadingContactPerson: finalData.loadingContactPerson,
    loadingPhone: finalData.loadingPhone,
    unloadingContactPerson: finalData.unloadingContactPerson,
    unloadingPhone: finalData.unloadingPhone,
    plannedLoadingDate: finalData.plannedLoadingDate,
    plannedUnloadingDate: finalData.plannedUnloadingDate,
    totalAmount: finalData.totalAmount,
    paymentMethod: finalData.paymentMethod,
    prepaymentCondition: finalData.prepaymentCondition,
    prepaymentPercent: finalData.prepaymentPercent,
    prepaymentDelayDays: finalData.prepaymentDelayDays,
    mainPaymentAmount:
      finalData.totalAmount && finalData.remainderPercent
        ? (Number(finalData.totalAmount) * Number(finalData.remainderPercent)) / 100
        : undefined,
    mainPaymentDelayDays: finalData.remainderDelayDays,
    additionalExpenses: [],
    shipperId: resolvedShipperId,
    consigneeId: resolvedConsigneeId,
    responsibleLogistId:
      finalData.responsibleLogistId ||
      (finalData.loadingPointType === 'application'
        ? applications.find((app) => app.id === Number(finalData.loadingPointId))?.responsibleLogistId
        : undefined) ||
      params.currentUserId,
    driverId: finalData.driverId,
    vehicleId: finalData.vehicleId,
    trailerVehicleId: finalData.trailerVehicleId,
    carrierId: finalData.carrierId,
    carrierContractRequestNumber: finalData.carrierContractRequestNumber,
    carrierContractRequestDate: finalData.carrierContractRequestDate,
    carrierContractRequestFile: finalData.carrierContractRequestFile,
    wagonNumber: finalData.wagonNumber,
    departureStation: finalData.departureStationId
      ? params.railwayStations?.find((s) => s.id === Number(finalData.departureStationId))?.name
      : undefined,
    arrivalStation: finalData.arrivalStationId
      ? params.railwayStations?.find((s) => s.id === Number(finalData.arrivalStationId))?.name
      : undefined,
    flightNumber: finalData.flightNumber,
    departureAirport: finalData.departureAirportId
      ? params.airports?.find((a) => a.id === Number(finalData.departureAirportId))?.name
      : undefined,
    arrivalAirport: finalData.arrivalAirportId
      ? params.airports?.find((a) => a.id === Number(finalData.arrivalAirportId))?.name
      : undefined
  };

  const stripped = stripTopologyKeys(raw);
  const out: Record<string, unknown> = { id: params.routeId };

  for (const [key, value] of Object.entries(stripped)) {
    if (key === 'id') continue;
    const beforeVal = (editingRoute as Record<string, unknown>)[key];
    if (normScalar(beforeVal) === normScalar(value)) continue;
    out[key] = value;
  }

  return out;
}
